import { useState, useEffect } from "react";
import { API , authFetch } from "@/App";
import {
  Users, MapPin, Clock, Navigation, Target, CheckCircle,
  CalendarDays, TrendingUp, ArrowRight
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import LiveTrackingMap from "@/components/LiveTrackingMap";

const FieldForceDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await authFetch(`${API}/field-force/dashboard`, {  });
        const json = await res.json();
        if (json.success) setData(json.data);
      } catch (err) {
        console.error("Failed to fetch field force data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 spinner" /></div>;
  if (!data) return <div className="text-center py-8 text-slate-500">No data available</div>;

  const { kpis, employee_stats } = data;

  const kpiCards = [
    { label: "Employees Present", value: kpis.present, sub: `of ${kpis.total_employees}`, icon: Users, color: "bg-emerald-500", link: "/field-force/attendance" },
    { label: "Employees Absent", value: kpis.absent, sub: `of ${kpis.total_employees}`, icon: Users, color: "bg-red-500", link: "/field-force/attendance" },
    { label: "Total Working Hours", value: kpis.total_working_hours, sub: "hrs today", icon: Clock, color: "bg-blue-500", link: "/field-force/attendance" },
    { label: "Tasks Completed", value: `${kpis.completed_tasks}/${kpis.total_tasks}`, sub: "today", icon: Target, color: "bg-amber-500", link: "/tasks" },
  ];

  return (
    <div className="space-y-6" data-testid="field-force-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Field Force Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">{data.date} &middot; Live field force overview</p>
        </div>
        <button
          onClick={() => navigate("/field-force/tracking")}
          className="flex items-center gap-2 px-4 py-2 bg-[#8B1A1A] text-white rounded-lg hover:bg-[#6B1515] text-sm font-medium transition-colors"
          data-testid="open-live-tracking-btn"
        >
          <MapPin className="w-4 h-4" /> Live Map
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        {kpiCards.map((kpi, i) => (
          <Card
            key={i}
            className="p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(kpi.link)}
            data-testid={`kpi-card-${i}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg ${kpi.color} flex items-center justify-center`}>
                <kpi.icon className="w-5 h-5 text-white" />
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300" />
            </div>
            <p className="text-2xl font-bold text-slate-900 font-mono">{kpi.value}</p>
            <p className="text-sm text-slate-500 mt-1">{kpi.label}</p>
          </Card>
        ))}
      </div>

      {/* Live Map */}
      <Card className="overflow-hidden" data-testid="ff-dashboard-live-map">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#8B1A1A] flex items-center justify-center">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white font-['Sora']">Live Field Map</h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Real-time GPS pings of all active field employees</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/field-force/tracking")}
            className="text-xs text-[#8B1A1A] dark:text-rose-400 hover:underline flex items-center gap-1 font-medium"
            data-testid="open-fullscreen-map"
          >
            Open full map <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="p-2">
          <LiveTrackingMap />
        </div>
      </Card>

      {/* Employee Performance Table */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 font-['Sora']">Employee Performance - Today</h3>
          <Badge variant="outline" className="text-xs">{employee_stats.length} employees</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="employee-performance-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Role</th>
                <th>Status</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Hours</th>
                <th>Distance (km)</th>
                <th>Stoppages</th>
                <th>Tasks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employee_stats.map((emp) => (
                <tr key={emp.employee_id}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#1A1A2E] flex items-center justify-center text-white text-xs font-semibold">
                        {emp.full_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{emp.full_name}</p>
                        <p className="text-xs text-slate-400">{emp.employee_code}</p>
                      </div>
                    </div>
                  </td>
                  <td><Badge variant="outline" className="text-xs">{emp.role?.replace(/_/g, " ")}</Badge></td>
                  <td>
                    {emp.status === "PRESENT" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Present</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 text-xs">Absent</Badge>
                    )}
                  </td>
                  <td className="text-sm">{emp.check_in_time || "—"}</td>
                  <td className="text-sm">{emp.check_out_time || "—"}</td>
                  <td className="text-sm font-mono">{emp.hours_on_field}h</td>
                  <td className="text-sm font-mono">{emp.distance_km}</td>
                  <td className="text-sm font-mono">{emp.stoppages}</td>
                  <td>
                    <span className="text-sm">
                      <span className="text-emerald-600 font-medium">{emp.tasks_completed}</span>
                      <span className="text-slate-400">/{emp.tasks_total}</span>
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => navigate(`/field-force/360?id=${emp.employee_id}`)}
                      className="text-xs text-[#8B1A1A] hover:underline flex items-center gap-1"
                      data-testid={`view-trail-${emp.employee_id}`}
                    >
                      <Navigation className="w-3 h-3" /> 360° View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default FieldForceDashboard;
